import React, { useMemo } from 'react';
import classNames from 'classnames';
import type { IProgressBarProps, IMetricConfig } from '../MetricBarCard/_type';
import { formatMetricValue } from '../MetricBarCard/_utils';
import { getName } from '@home/utils/common';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { handleColorToRGBA } from '@/pages/datalab/utils/utils';
import { dynamicString } from '@home/utils/dynamicString';
import funnelInfoOne from '@/pages/dashboard/assets/svg/funnel-info-one.svg';
import funnelInfoTwo from '@/pages/dashboard/assets/svg/funnel-info-two.svg';
import funnelInfoThree from '@/pages/dashboard/assets/svg/funnel-info-three.svg';
import funnelInfoFour from '@/pages/dashboard/assets/svg/funnel-info-four.svg';

import './index.scss';
import CommonBubble from '@home/components/CommonBubble';
import FormatHtmlTextToReact from '@home/components/FormatHtmlTextToReact';

const DEFAULT_INFO_ARR = [funnelInfoOne, funnelInfoTwo, funnelInfoThree, funnelInfoFour];

const ProgressBar: React.FC<IProgressBarProps> = ({
  item,
  index,
  infoArr: propsInfoArr,
  data,
  defaultColor,
  minWidth,
  metricCardData = [],
  componentMetrics = [],
}) => {
  const {
    labelEn,
    labelCn,
    color,
    metric1,
    extraMetrics = [],
    extraMetricsSeparator = '&',
    infoIconColor,
    metric2,
  } = item;
  const infoArr = propsInfoArr || DEFAULT_INFO_ARR;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  // 从 componentMetrics 中获取指标配置
  const metric1Config: IMetricConfig = componentMetrics.find(m => m.metricKey === metric1.metricKey) || metric1;
  const metric2Config: IMetricConfig = componentMetrics.find(m => m.metricKey === metric2.metricKey) || metric2;
  const { nameEn, nameCn, explainEn, explainCn, haveInfo = true, preUnit, endUnit } = metric1Config;

  // extraMetrics 配置列表
  const extraMetricConfigs: IMetricConfig[] = useMemo(
    () => extraMetrics.map(
      (em) => componentMetrics.find(m => m.metricKey === em.metricKey) || em,
    ),
    [extraMetrics, componentMetrics],
  );

  // 从 metricCardData 中获取指标值
  const metric1Data = useMemo(
    () => metricCardData.find(d => d.metrics_name === metric1.metricKey) || { value: '' },
    [metricCardData, metric1.metricKey],
  );

  const extraMetricDataList = useMemo(
    () => extraMetrics.map(
      (em) => metricCardData.find(d => d.metrics_name === em.metricKey) || { value: '' },
    ),
    [metricCardData, extraMetrics],
  );

  const metric2Data = useMemo(
    () => metricCardData.find(d => d.metrics_name === metric2.metricKey) || { value: '' },
    [metricCardData, metric2.metricKey],
  );

  // 获取指标名称（国际化）
  const metric1Name = getName(isEn, dynamicString(nameEn, data), dynamicString(nameCn, data)) || metric1Config.name;
  const explain = getName(isEn, dynamicString(explainEn, data), dynamicString(explainCn, data)) || metric1Config.explain;
  const label = getName(isEn, labelEn, labelCn);
  const metric2FormattedValue = formatMetricValue(metric2Config, metric2Data.value || '', data);
  const metric2Explain = getName(isEn, dynamicString(metric2Config.explainEn, data), dynamicString(metric2Config.explainCn, data));

  // 计算百分比
  const percentValue = useMemo(() => {
    const { value } = metric1Data;
    if (value === null || value === undefined || value === '') return 0;
    const numValue = Number(value);
    if (isNaN(numValue)) return 0;
    return numValue * 100;
  }, [metric1Data]);

  const percentDisplay = formatMetricValue(metric1Config, metric1Data.value || '', data);

  // 格式化 extraMetrics 的值列表
  const extraMetricDisplays: string[] = useMemo(
    () => extraMetricConfigs.map((config, idx) => {
      const metricData = extraMetricDataList[idx];
      return formatMetricValue(config, metricData?.value || '', data);
    }),
    [extraMetricConfigs, extraMetricDataList, data],
  );

  // 确定使用的颜色
  const barColor = color || defaultColor || ['#2064ff', '#4d8fff'];
  const gradientColors = Array.isArray(barColor) ? barColor : [barColor, barColor];

  const gradientStyle: React.CSSProperties = {
    background: `linear-gradient(90deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`,
    width: `${Math.min(100, Math.max(0, percentValue))}%`,
  };

  const metricStyle: React.CSSProperties = {
    color: gradientColors[0],
    backgroundColor: handleColorToRGBA({ hexa: gradientColors[0], alpha: "12" }),
    borderColor: handleColorToRGBA({ hexa: gradientColors[0], alpha: "24" }),
  };

  // info icon 渲染：配了 infoIconColor 走动态 inline SVG，否则用默认 SVG 图标
  const renderInfoIcon = () => {
    if (!haveInfo) return null;
    if (infoIconColor) {
      return (
        <svg className='dbd-metric-bar-card__bar-metrics-info' width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16Z" fill={infoIconColor} />
          <path d="M9 12C9 12.5523 8.55228 13 8 13C7.44771 13 7 12.5523 7 12L7 7C7 6.44771 7.44772 6 8 6C8.55228 6 9 6.44772 9 7L9 12Z" fill="white" />
          <path d="M9 4C9 4.55229 8.55228 5 8 5C7.44771 5 7 4.55228 7 4C7 3.44772 7.44772 3 8 3C8.55228 3 9 3.44772 9 4Z" fill="white" />
        </svg>
      );
    }
    return <img className='dbd-metric-bar-card__bar-metrics-info' src={infoArr[(index || 0) % infoArr.length]} />;
  };

  return (
    <div className={classNames('dbd-metric-bar-card__bar-item')}>
      <div className="dbd-metric-bar-card__bar-header">
        <div>
          <span className="dbd-metric-bar-card__bar-label">{label}</span>
        </div>
        <CommonBubble content={<FormatHtmlTextToReact text={explain} />}>
          <div className="dbd-metric-bar-card__bar-metrics" style={metric1Config?.style || metricStyle || {}}>
            <div className='dbd-metric-bar-card__bar-metrics-name'>
              {metric1Name}
            </div>
            <div className="dbd-metric-bar-card__bar-metrics-value">
              {preUnit || ''}
              {percentDisplay}
              {endUnit || ''}
              {extraMetricDisplays.map((display, idx) => (
                <span key={idx}>
                  &nbsp;{extraMetricsSeparator}&nbsp;{display}
                </span>
              ))}
            </div>
            {renderInfoIcon()}
          </div>
        </CommonBubble>
      </div>
      <div className='dbd-metric-bar-card__bar-progress-flex'>
        <div className="dbd-metric-bar-card__bar-progress-wrapper">
          <div className="dbd-metric-bar-card__bar-progress" style={gradientStyle} />
        </div>
        <CommonBubble content={<FormatHtmlTextToReact text={metric2Explain} />}>
          <div className="dbd-metric-bar-card__bar-progress-value" style={{ minWidth }}>
            {metric2FormattedValue === '-' ? "" : metric2Config.preUnit}
            {metric2FormattedValue}
            {metric2FormattedValue === '-' ? "" : metric2Config.endUnit}
          </div>
        </CommonBubble>
      </div>
    </div>
  );
};

export default ProgressBar;
